// @ts-strict-ignore
import _ from 'lodash';
import { InitializeMode, PersistenceLevel, Store } from '@/core/flux.service';
import { getUniqueId } from '@/trendData/trend.utilities';

/**
 * A store for containing annotations which are listed in the panel. An annotation is composed of a description
 * and a collection of interests that it annotates.
 */

export class AnnotationStore extends Store {
  static readonly storeName = 'sqAnnotationStore';
  persistenceLevel: PersistenceLevel = 'WORKBOOK';

  initialize(initializeMode: InitializeMode) {
    const saveState = this.state && initializeMode !== 'FORCE';
    this.state = this.immutable({
      id: undefined,
      document: '',
      isDiscoverable: false,
      isExpanded: false,
      width: 0,
      height: 0,
      isCommentsExpanded: false,
      isAnnotatesExpanded: false,
      annotations: saveState ? this.state.get('annotations') : [],
      annotatedItemIds: this.monkey(['annotations'], function (annotations) {
        return _.transform(
          annotations,
          function (map, annotation: any) {
            _.forEach(annotation.interests, function (interest: any) {
              map[interest.item.id] = annotation.id;
              if (interest.capsule) {
                const key = getUniqueId(interest.item.id, interest.capsule.id);
                map[key] = annotation.id;
              }
            });
          },
          {},
        );
      }),
      annotationCapsuleSet: undefined,
      highlightId: '',
    });
  }

  get id() {
    return this.state.get('id');
  }

  get document() {
    return this.state.get('document');
  }

  get isDiscoverable() {
    return this.state.get('isDiscoverable');
  }

  get isExpanded() {
    return this.state.get('isExpanded');
  }

  get width() {
    return this.state.get('width');
  }

  get height() {
    return this.state.get('height');
  }

  get isCommentsExpanded() {
    return this.state.get('isCommentsExpanded');
  }

  get isAnnotatesExpanded() {
    return this.state.get('isAnnotatesExpanded');
  }

  get annotations() {
    return this.state.get('annotations');
  }

  get annotatedItemIds() {
    return this.state.get('annotatedItemIds');
  }

  get annotationCapsuleSet() {
    return this.state.get('annotationCapsuleSet');
  }

  get highlightId() {
    return this.state.get('highlightId');
  }

  get isCkEnabled() {
    return this.state.get('isCkEnabled');
  }

  /**
   * Filters a collection of annotations to only those that are a journal entry for the specified worksheet.
   *
   * @param {Object[]} annotations - The collection to filter
   * @param {String} worksheetId - The worksheet
   * @returns The filtered collection of annotations
   */
  findJournalEntries(annotations, worksheetId) {
    return _.filter(
      annotations,
      (annotation: any) => !annotation.discoverable && _.some(annotation.interests, ['item.id', worksheetId]),
    );
  }

  /**
   * Exports state so it can be used to re-create the state later using `rehydrate`.
   *
   * @returns {Object} The dehydrated items.
   */
  dehydrate() {
    return _.omit(this.state.serialize(), [
      'annotations',
      'annotationCapsuleSet',
      'document',
      'highlightId',
      'isDiscoverable',
    ]);
  }

  /**
   * Re-creates the annotations. All necessary data needed to rehydrate is persisted so no actions have to be
   * called.
   *
   * @param {Object} dehydratedState Previous state usually obtained from `dehydrate` method.
   */
  rehydrate(dehydratedState) {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    /**
     * Sets the id of the loaded annotation.
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.id - The id
     */
    ANNOTATION_SET_ID: (payload) => {
      this.state.set('id', payload.id);
    },

    /**
     * Sets the document of the loaded annotation.
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.document - The document
     */
    ANNOTATION_SET_DOCUMENT: (payload) => {
      this.state.set('document', payload.document);
    },

    /**
     * Sets the isDiscoverable mode flag
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.isDiscoverable - The isDiscoverable mode flag
     */
    ANNOTATION_SET_IS_DISCOVERABLE: (payload) => {
      this.state.set('isDiscoverable', payload.isDiscoverable);
    },

    /**
     * Sets the isExpanded state
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.expand - The isExpanded state
     */
    ANNOTATION_SET_IS_EXPANDED: (payload) => {
      this.state.set('isExpanded', payload.expand);
    },

    /**
     * Sets the width state
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.expand - The width state
     */
    ANNOTATION_SET_WIDTH: (payload) => {
      this.state.set('width', payload.width);
      this.state.commit();
    },

    /**
     * Sets the height state
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.expand - The height state
     */
    ANNOTATION_SET_HEIGHT: (payload) => {
      this.state.set('height', payload.height);
      this.state.commit();
    },

    /**
     * Sets the isCommentsExpanded state
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.expand - The isCommentsExpanded state
     */
    ANNOTATION_SET_IS_COMMENTS_EXPANDED: (payload) => {
      this.state.set('isCommentsExpanded', payload.expand);
    },

    /**
     * Sets the isAnnotatesExpanded state
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.expand - The isAnnotatesExpanded state
     */
    ANNOTATION_SET_IS_ANNOTATES_EXPANDED: (payload) => {
      this.state.set('isAnnotatesExpanded', payload.expand);
    },

    /**
     * Sets the list of annotations.
     *
     * @param {Object} payload - Object container for arguments
     * @param {Object[]} payload.annotations - The annotations
     */
    ANNOTATION_SET_ANNOTATIONS: (payload) => {
      this.state.set('annotations', payload.annotations);
    },

    /**
     * Replaces an annotation in this list of annotations, or adds it to the list if not already present.
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.id - The ID of the annotation to set
     * @param {Object} payload.annotation - The annotation
     */
    ANNOTATION_SET_ANNOTATION: (payload) => {
      const cursor = this.state.select('annotations');
      const index = _.findIndex(cursor.get(), ['id', payload.id]);
      if (index >= 0) {
        cursor.splice([index, 1, payload.annotation]);
      } else {
        cursor.push(payload.annotation);
      }
    },

    /**
     * Sets the capsule set that is used to store annotations in this worksheet
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.id - ID of the capsule set
     * @param {Number} payload.maximumDuration - Duration of the largest annotation that has been stored in
     *   this capsule set, in milliseconds
     */
    ANNOTATION_SET_CAPSULE_SET: (payload) => {
      this.state.set('annotationCapsuleSet', {
        id: payload.id,
        maximumDuration: payload.maximumDuration,
      });
    },

    /**
     * Sets the id of the annotation that should be highlighted
     *
     * @param {Object} payload - Object container for arguments
     * @param {String} payload.id - ID of the annotation to be highlighted
     */
    ANNOTATION_SET_HIGHLIGHT_ID: (payload) => {
      this.state.set('highlightId', payload.id);
    },

    ANNOTATION_SET_IS_CK_ENABLED: (payload: { isCkEnabled: boolean }) => {
      this.state.set('isCkEnabled', payload.isCkEnabled);
    },
  };
}
